import { useEffect } from 'react'
import { useModelStore, useSelectionStore } from '@editorStores'

/** All selectable element guids: elements, position guids (rips/lintels) and slab beam guids. */
const collectElementGuids = (model: PlanarModel): Set<string> => {
  const guids = new Set<string>()

  model.walls.forEach(wall => guids.add(wall.guid))
  model.slabs.forEach(slab => guids.add(slab.guid))
  model.roof_slabs.forEach(roofSlab => guids.add(roofSlab.guid))
  model.beams.forEach(beam => guids.add(beam.guid))
  model.columns.forEach(column => guids.add(column.guid))
  model.purlins.forEach(purlin => guids.add(purlin.guid))
  model.vertical_slabs.forEach(verticalSlab => guids.add(verticalSlab.guid))
  model.vertical_roof_slabs.forEach(verticalRoofSlab => guids.add(verticalRoofSlab.guid))
  model.foundationElements.forEach(foundation => guids.add(foundation.guid))

  model.rips.forEach(rip => guids.add(rip.position_guid))
  model.lintels.forEach(lintel => guids.add(lintel.position_guid))

  model.vertical_slabs.forEach(slab => {
    if (slab.beam?.guid) guids.add(slab.beam.guid)
  })
  model.vertical_roof_slabs.forEach(slab => {
    if (slab.beam?.guid) guids.add(slab.beam.guid)
  })

  return guids
}

const collectOpeningGuids = (model: PlanarModel): Set<string> => {
  const guids = new Set<string>()
  model.walls.forEach(wall => wall.openings?.forEach((o: Opening) => guids.add(o.guid)))
  return guids
}

/**
 * Prunes guids from the global selection that no longer exist in the model (deleted or
 * regenerated elements). Since the selection now survives page and floorplan/3D switches, it can
 * outlive the elements it points at — without this, downstream code would resolve dead guids.
 *
 * Only planar selections and opening sub-selections are pruned: stiffening/member-position guids
 * have no synchronous source of truth here (their lists live in per-page query data), so pruning
 * them would clear valid selections while that data is still loading; their consumers ignore
 * unknown guids instead.
 */
const SelectionModelSync = (): null => {
  const model = useModelStore(state => state.model)

  useEffect(() => {
    // Bail out before any model traversal in the common case of nothing being selected — this
    // effect runs on every model write anywhere in the app.
    const { selection, subSelection, select } = useSelectionStore.getState()
    const planarSelection = selection?.type === 'planar' ? selection : null
    const openingSub = subSelection?.type === 'opening' ? subSelection : null
    if (!planarSelection && !openingSub) return

    const openingGuids = collectOpeningGuids(model)

    if (planarSelection) {
      const validGuids = collectElementGuids(model)
      openingGuids.forEach(guid => validGuids.add(guid))

      const guids = planarSelection.guids.filter(guid => validGuids.has(guid))
      if (guids.length !== planarSelection.guids.length) {
        // Pruning is not a user selection: keep the mode (`adopt: 'keep'`) rather than adopting one.
        select(guids.length ? { ...planarSelection, guids } : null, { adopt: 'keep' })
      }
    }

    // Pruning the primary may already have discarded the sub-selection, so re-read it.
    const { subSelection: currentSub, selectSub } = useSelectionStore.getState()
    if (currentSub?.type === 'opening') {
      const guids = currentSub.guids.filter(guid => openingGuids.has(guid))
      if (guids.length !== currentSub.guids.length)
        selectSub(guids.length ? { ...currentSub, guids } : null)
    }
  }, [model])

  return null
}

export default SelectionModelSync
